import Paragraph from '@/components/shared/typography/Paragraph'
import ConfirmationDialog from '@/javascript/presentation/components/modals/confirmation-dialog'
import ErrorBox from '@/components/shared/boxes/ErrorBox'
import ErrorTable from '@/javascript/integration/erp/shared/ErrorTable'
import { Dialog, DialogTitle, Divider, DialogContent, Box, DialogActions, Button, Tooltip } from '@mui/material';
import React, { FormEvent, useEffect, useState } from 'react';

interface SyncErrorsModalProps {
  modalOpen: boolean
  onClose: (s: boolean) => void
  onSuccess: (responseJson: any) => {}
  tryAgainApiUrl?: string
  tryAgainApiMethod?: string
  errorsHref: {
    recentErrorHref?: string;
    customerErrorHref?: string;
    partErrorHref?: string;
    serviceErrorHref?: string;
    workErrorHref?: string;
  };
  canRetrySync?: boolean;
}



const SyncErrorsModal: React.FC<SyncErrorsModalProps> = ({ ...props }) => {
  const { modalOpen, onClose, onSuccess, tryAgainApiUrl, tryAgainApiMethod, errorsHref, canRetrySync,} = props;
  const [apiError, setApiError] = useState<{ errorMessage?: string }>({});
  const [openConfirmationDialog, updateConfirmationDialogVisibility] = useState<boolean>(false);
  const errorsKeys = Object.keys(errorsHref).filter((k): k is keyof typeof errorsHref => !!errorsHref[k as keyof typeof errorsHref]);

  const defineTableTitleByErrorKey = {
    recentErrorHref: 'Erros recentes',
    customerErrorHref: 'Erros - Clientes',
    partErrorHref: 'Erros - Peças / Produtos',
    serviceErrorHref: 'Erros - Serviços',
    workErrorHref: 'Erros - Atividades / Ordens de Serviço',
  }

  const tryAgain = async () => {
    try {
      const response = await fetch(tryAgainApiUrl || '', {
        method: tryAgainApiMethod,
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
      })

      const contentType = response.headers.get('content-type')

      if (!contentType || !contentType.includes('application/json')) {
        throw new TypeError("Oops, we haven't got JSON!")
      }

      const responseJson = await response.json()

      if (!response.ok) {
        const errorMessage = `Error ${response.status}: ${responseJson.error}`
        updateConfirmationDialogVisibility(false)
        return setApiError({ errorMessage: errorMessage })
      }

      updateConfirmationDialogVisibility(false)
      onClose(false)
      onSuccess(responseJson)
    } catch (error) {
      console.error(error)
    }
  }

  useEffect(() => {
    return () => {
      setApiError({})
    }
  }, [])

  return (
    <>
      <Dialog
        fullWidth
        open={modalOpen}
        onClose={() => onClose(false)}
        maxWidth="md"
      >
        <Box sx={{ padding: "16px", position: "relative" }}>
          <DialogTitle sx={{ textAlign: "center" }}>Erros</DialogTitle>
        </Box>

        <Divider />

        <DialogContent
          sx={{
            padding: "16px",
            display: "flex",
            flexDirection: "column",
            gap: "64px",
          }}
        >
          {apiError.errorMessage && ( 
            <ErrorBox errorMessage={apiError.errorMessage} onClose={() => setApiError({})} />
          )}

          {errorsKeys.map((key) => (
            <ErrorTable
              key={key}
              tableTitle={defineTableTitleByErrorKey[key]}
              entityName={key}
              apiUrl={errorsHref[key]}
            />
          ))}
        </DialogContent>
        <Divider />
        <DialogActions sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 3 }}>
          <Button
            variant="text"
            size="medium"
            color="error"
            onClick={() => onClose(false)}
            type="button"
            autoFocus={false}
          >
            Cancelar
          </Button>
          <Tooltip
            arrow
            placement="top"
            title={canRetrySync ? "" : "Sem permissão para realizar a ação."}
          >
            <span>
              <Button
                id="omie-disconnect-modal-disconnect-btn"
                variant="contained"
                size="medium"
                color="success"
                type="button"
                autoFocus={false}
                onClick={() => updateConfirmationDialogVisibility(true)}
                disabled={openConfirmationDialog || !canRetrySync}
              >
                Tentar novamente
              </Button>
            </span>
          </Tooltip>
        </DialogActions>

        <ConfirmationDialog
          modalOpen={openConfirmationDialog}
          onClose={(v) => updateConfirmationDialogVisibility(v)}
          onSubmit={() => tryAgain()}
          confirmationBtnProps={{ title: 'Já corrigi os erros' }}
        >
          <Box sx={{ display: "flex", flexDirection: "column", gap: "16px" }}>
            <Paragraph>
              Os erros precisam ser corrigidos antes de tentar vincular os
              sistemas novamente
            </Paragraph>
          </Box>
        </ConfirmationDialog>
      </Dialog>
    </>
  );
};

export default SyncErrorsModal;
